import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { EyeSlash, Notification, Shield, Refresh2 } from 'iconsax-react'

import { useAuth } from '@/core/auth/hooks/useAuth'
import { profileService } from '@/features/profile/services/profile.service'

const effectIcons = { profile: EyeSlash, notifications: Notification, sessions: Shield, reactivation: Refresh2 }

export function SuspendAccountPage() {
  const { t, i18n } = useTranslation('profile')
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [suspended, setSuspended] = useState(false)

  const suspendAccount = async () => {
    setError(null)
    setIsSubmitting(true)
    try {
      await profileService.suspendAccount(
        {},
        i18n.language.startsWith('en') ? 'en' : 'es',
      )
      setSuspended(true)
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : t('suspension.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-3 text-[#273d4a]">
      <h1 className="m-0 text-center text-[1.1rem] font-semibold text-[#003d66]">{t('suspension.title')}</h1>

      <div className="mt-8 w-full max-w-xl rounded-md bg-[#fff9e0] px-6 py-7 text-center text-[#5d541c]">
        <svg className="mx-auto h-7 w-7" fill="none" height="20" viewBox="0 0 20 20" width="20" aria-hidden="true">
          <path
            d="M8.575 3.217 1.517 15a1.667 1.667 0 0 0 1.425 2.5h14.117a1.667 1.667 0 0 0 1.425-2.5L11.425 3.217a1.667 1.667 0 0 0-2.85 0Z"
            fill="#FFDB43"
          />
          <path d="M10 7.5v3.333" stroke="black" strokeLinecap="round" strokeWidth="1.2" />
          <circle cx="10" cy="14.167" fill="black" r="0.625" />
        </svg>
        <h2 className="mt-2 text-sm font-semibold text-[#dfb400]">{t('suspension.warningTitle')}</h2>
        <p className="mx-auto mt-2 max-w-md text-xs leading-5">{t('suspension.warning')}</p>
      </div>

      <div className="mt-8 w-full max-w-xl">
        <p className="mt-2 text-xs font-medium text-[#273d4a]">{t('suspension.effectsTitle')}</p>
        <ul className="mt-3 flex list-none flex-col gap-2 p-0 text-xs leading-5 text-[#526b7b]">
          {(['profile', 'notifications', 'sessions', 'reactivation'] as const).map((key) => {
            const Icon = effectIcons[key]
            return (
              <li key={key} className="flex items-start gap-2">
                <Icon size="16" color="currentColor" variant="Linear" className="mt-0.5 shrink-0 text-[#007acc]" aria-hidden="true" />
                <span>{t(`suspension.effects.${key}`)}</span>
              </li>
            )
          })}
        </ul>

        {error && <p className="mt-5 text-center text-xs text-[#b4232f]" role="alert">{error}</p>}

        <div className="mt-10 flex flex-col items-center gap-4">
          <button
            type="button"
            disabled={isSubmitting || suspended}
            onClick={() => void suspendAccount()}
            className="h-10 w-full max-w-xs cursor-pointer rounded-md bg-[#007acc] px-5 text-xs font-semibold text-white hover:bg-[#0066aa] disabled:cursor-not-allowed disabled:bg-[#d7e0e7] disabled:text-[#8c9aa4]"
          >
            {isSubmitting ? t('suspension.submitting') : t('suspension.submit')}
          </button>
          <button type="button" disabled={isSubmitting} onClick={() => navigate('/perfil')} className="cursor-pointer text-xs text-[#526b7b] underline disabled:cursor-not-allowed">{t('suspension.back')}</button>
        </div>
      </div>

      {suspended && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#05141f]/25 px-4" role="dialog" aria-modal="true" aria-labelledby="suspension-success-title">
          <div className="w-full max-w-sm rounded-md bg-[#eef7fc] px-7 py-6 text-center shadow-xl">
            <svg className="mx-auto h-8 w-8" fill="none" height="20" viewBox="0 0 20 20" width="20" aria-hidden="true">
              <path
                d="M8.575 3.217 1.517 15a1.667 1.667 0 0 0 1.425 2.5h14.117a1.667 1.667 0 0 0 1.425-2.5L11.425 3.217a1.667 1.667 0 0 0-2.85 0Z"
                fill="#FFDB43"
              />
              <path d="M10 7.5v3.333" stroke="black" strokeLinecap="round" strokeWidth="1.2" />
              <circle cx="10" cy="14.167" fill="black" r="0.625" />
            </svg>
            <h2 id="suspension-success-title" className="mt-3 text-[26px] font-semibold text-[#273d4a]">{t('suspension.successTitle')}</h2>
            <p className="mt-3 text-body-2 text-[#526b7b]">{t('suspension.success')}</p>
            <button type="button" onClick={() => void logout()} className="mt-6 h-10 w-full cursor-pointer rounded-sm bg-[#FFDB43] px-5 text-body-2 font-semibold text-[#273d4a] hover:bg-[#f3c900]">{t('suspension.accept')}</button>
          </div>
        </div>
      )}
    </div>
  )
}
